/**
 * Which region holds this browser's open purchase intent (ESCLOUD-332).
 *
 * An intent is created on the API of the region the buyer chose, before any
 * tenant exists, so nothing else in the SPA knows where to look for it. The
 * hint is a routing convenience only: the server answers whether the intent
 * still exists, and a stale hint costs one 404 and a redirect to pricing.
 */

const STORAGE_KEY = 'escloud-purchase-intent';

export interface IPurchaseIntentHint {
    region: string;
    intentId: string;
    planId: string;
}

export function readPurchaseIntentHint(): IPurchaseIntentHint | null {
    if (typeof window === 'undefined') return null;

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;

        const { region, intentId, planId } = parsed as Record<string, unknown>;
        if (typeof region !== 'string' || typeof intentId !== 'string' || typeof planId !== 'string')
            return null;

        const code = region.trim().toLowerCase();
        if (!code || !intentId || !planId) return null;

        return { region: code, intentId, planId };
    } catch {
        // Corrupted store, so routing falls back to pricing.
        return null;
    }
}

export function writePurchaseIntentHint(hint: IPurchaseIntentHint): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...hint, region: hint.region.trim().toLowerCase() })
        );
    } catch {
        // Storage full or blocked (Safari private mode): losing the hint costs a
        // redirect through pricing, so it must never propagate.
        return;
    }
}

export function clearPurchaseIntentHint(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        return;
    }
}
